import * as React from "react"

import {
	AlertVariant,
	Button,
	Spinner,
	useSnackbar,
} from "@walmart-web/livingdesign-components"
import { CloudDownload } from "@walmart-web/livingdesign-icons"
import { useIntl } from "react-intl"

import { getSelloutReportFile } from "components/Transactional/SelloutReport/service"
import { Message } from "components/Shared/AlertMessage"
import { downloadReport } from "helpers/download.utils"
import { getMailTo } from "helpers/contact"
import { EMAIL_SUPPORT_CL } from "constants/contact"
import "./index.scss"

enum DownloadMessageType {
	Success,
	Error,
}
const subject = "Error en módulo de Sell Out"
const lineBreak = "%0D%0A"
const space = "%20"

const DownloadMessages: Map<DownloadMessageType, Message> = new Map([
	[
		DownloadMessageType.Error,
		{
			id:
				"phronesis.sellout_report.application.reporting_sellout.error.download",
			values: {
				mailto:
					"phronesis.sellout_report.application.reporting_sellout.mailto.error",
			},
			type: "error" as AlertVariant,
		},
	],
])

interface DownloadProps {
	filename: string
	setMessage: (message?: Message) => void
	setDisableDownload: (disableDownload: boolean) => void
	disableDownload: boolean
}

const Download: React.FC<DownloadProps> = ({
	filename,
	setMessage,
	setDisableDownload,
	disableDownload,
}): JSX.Element => {
	const [isFetching, setFetching] = React.useState(false)
	const intl = useIntl()
	const spinnerProps = { height: "38px", width: "38px" }
	const { addSnack } = useSnackbar()
	const resetDownload = () => {
		setDisableDownload(false)
		setFetching(false)
	}

	const beginDownload = () => {
		setDisableDownload(true)
		setFetching(true)
		setMessage(undefined)
	}

	const getSuccessMessage = () => {
		return intl.formatMessage({
			id:
				"phronesis.sellout_report.application.reporting_sellout.success.download",
		})
	}

	const getDownloadErrorMessage = (error: any) => {
		const message = DownloadMessages.get(DownloadMessageType.Error)
		if (message) {
			const body = intl.formatMessage(
				{ id: "phronesis.error.report.body" },
				{
					space,
					lineBreak,
					name: error.name,
					message: error.message,
					stack: error.stack,
				}
			)
			message.values = {
				mailto: (str: string) => (
					<Button
						UNSAFE_className="mailto"
						variant="tertiary"
						onClick={() => {
							getMailTo(EMAIL_SUPPORT_CL, subject, body)
						}}
					>
						{str}
					</Button>
				),
			}
		}
		return message
	}

	const onDownloadFile = (filename: string) => async () => {
		try {
			beginDownload()
			const requestedFileBlob = await getSelloutReportFile({ id: filename })
			downloadReport(requestedFileBlob, filename)
			addSnack({
				message: getSuccessMessage(),
			})
			resetDownload()
		} catch (e: any) {
			resetDownload()
			setMessage(getDownloadErrorMessage(e))
		}
	}
	return (
		<div className="download-icon-container" role="button">
			{isFetching ? (
				<Spinner
					UNSAFE_className="download-spinner"
					spinnerProps={spinnerProps}
					color="gray"
					size="small"
				/>
			) : (
				<Button
					UNSAFE_className="download-button"
					onClick={onDownloadFile(filename)}
					disabled={disableDownload}
					variant="tertiary"
					leading={<CloudDownload />}
				>
					{intl.formatMessage({
						id:
							"phronesis.sellout_report.application.reporting_sellout.table.header.download",
					})}
				</Button>
			)}
		</div>
	)
}

export default Download
